import React, { useState, useEffect } from 'react'
import {
  FaMapMarkedAlt,
  FaEye,
  FaCheck,
  FaTimes,
  FaTrash,
  FaClock,
  FaCar,
  FaHotel,
  FaRunning,
} from 'react-icons/fa'
import customTripService from '../services/customTripService'
import { toast } from 'react-toastify'
import Loader from './Loader'

const CustomTripRequests = () => {
  const [trips, setTrips] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedTrip, setSelectedTrip] = useState(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [statusFilter, setStatusFilter] = useState('all')
  const [adminNotes, setAdminNotes] = useState('')

  useEffect(() => {
    loadTrips()
  }, [statusFilter])

  const loadTrips = async () => {
    try {
      setLoading(true)
      const params = statusFilter !== 'all' ? { status: statusFilter } : {}
      const data = await customTripService.getAllCustomTrips(params)
      setTrips(data.trips || [])
    } catch (error) {
      console.error('Error loading custom trips:', error)
      const errorMessage =
        error.response?.data?.message || error.message || 'Error loading custom trips'
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const viewDetails = async (tripId) => {
    try {
      const data = await customTripService.getCustomTripById(tripId)
      setSelectedTrip(data.trip)
      setAdminNotes(data.trip.admin_notes || '')
      setShowDetailModal(true)
    } catch (error) {
      console.error('Error loading trip details:', error)
      const errorMessage =
        error.response?.data?.message || error.message || 'Error loading trip details'
      toast.error(errorMessage)
    }
  }

  const updateStatus = async (status) => {
    if (!selectedTrip) return

    try {
      await customTripService.updateTripStatus(selectedTrip.id, status, adminNotes)
      toast.success(`Trip ${status} successfully`)
      setShowDetailModal(false)
      loadTrips()
    } catch (error) {
      console.error('Error updating status:', error)
      toast.error('Error updating trip status')
    }
  }

  const deleteTrip = async (tripId) => {
    if (!window.confirm('Are you sure you want to delete this trip request?')) return

    try {
      await customTripService.deleteCustomTrip(tripId)
      toast.success('Trip deleted successfully')
      setShowDetailModal(false)
      loadTrips()
    } catch (error) {
      console.error('Error deleting trip:', error)
      toast.error('Error deleting trip')
    }
  }

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
    }
    return badges[status] || 'bg-gray-100 text-gray-800'
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center">
            <FaMapMarkedAlt className="mr-2 text-primary-600" />
            Custom Trip Requests
          </h2>
          <p className="text-gray-600 mt-1">Review and manage custom trip plans from customers</p>
        </div>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input-field w-48"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {loading ? (
        <Loader />
      ) : trips.length === 0 ? (
        <div className="card p-12 text-center">
          <FaMapMarkedAlt className="text-6xl text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No custom trip requests found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {trips.map((trip) => (
            <div key={trip.id} className="card p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-gray-900">{trip.trip_name}</h3>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(trip.status)}`}
                    >
                      {trip.status.toUpperCase()}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div>
                      <p className="text-sm text-gray-500">Customer</p>
                      <p className="font-semibold">{trip.user_name || 'Guest'}</p>
                      <p className="text-xs text-gray-600">{trip.user_email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Locations</p>
                      <p className="font-semibold">{trip.location_count || 0} stops</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Distance</p>
                      <p className="font-semibold">{trip.total_distance || 0} km</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Cost</p>
                      <p className="font-semibold text-green-600">
                        ₹{trip.total_cost ? trip.total_cost.toLocaleString() : '0'}
                      </p>
                    </div>
                  </div>

                  {trip.trip_description && (
                    <p className="text-sm text-gray-600 mt-3 line-clamp-2">
                      {trip.trip_description}
                    </p>
                  )}

                  <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
                    <FaClock className="mr-1" />
                    Submitted: {new Date(trip.created_at).toLocaleDateString()}
                    {trip.car_name && (
                      <>
                        <span className="mx-2">•</span>
                        <FaCar className="mr-1" />
                        {trip.car_name}
                      </>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => viewDetails(trip.id)}
                  className="btn-primary flex items-center ml-4"
                >
                  <FaEye className="mr-2" />
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedTrip && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold">{selectedTrip.trip_name}</h3>
                <span
                  className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mt-2 ${getStatusBadge(selectedTrip.status)}`}
                >
                  {selectedTrip.status.toUpperCase()}
                </span>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Customer Info */}
              <div className="card p-4 bg-gray-50">
                <h4 className="font-bold mb-3">Customer Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Name</p>
                    <p className="font-semibold">{selectedTrip.user_name || 'Guest User'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Email</p>
                    <p className="font-semibold">{selectedTrip.user_email || 'N/A'}</p>
                  </div>
                  {selectedTrip.user_phone && (
                    <div>
                      <p className="text-gray-600">Phone</p>
                      <p className="font-semibold">{selectedTrip.user_phone}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Trip Summary */}
              <div className="card p-4 bg-blue-50">
                <h4 className="font-bold mb-3">Trip Summary</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Total Distance</p>
                    <p className="text-xl font-bold text-blue-600">
                      {selectedTrip.total_distance || 0} km
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Price per KM</p>
                    <p className="text-xl font-bold">₹{selectedTrip.price_per_km || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Mileage Charges</p>
                    <p className="text-xl font-bold text-green-600">
                      ₹{selectedTrip.mileage_charges || 0}
                    </p>
                  </div>
                </div>
              </div>

              {/* Route Details */}
              <div>
                <h4 className="font-bold mb-3">
                  Route ({selectedTrip.locations?.length || 0} Locations)
                </h4>
                <div className="space-y-3">
                  {selectedTrip.locations?.map((loc, index) => (
                    <div key={loc.id} className="card p-4">
                      <div className="flex items-start gap-3">
                        <span className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="font-bold text-lg">{loc.location_name}</h5>
                            {index === 0 && (
                              <span className="bg-green-500 text-white text-xs px-2 py-1 rounded">
                                Base Location
                              </span>
                            )}
                          </div>
                          {index > 0 && loc.distance_from_previous && (
                            <p className="text-sm text-blue-600 mb-2">
                              {loc.distance_from_previous} km from previous location
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mb-3">
                            Coordinates: {loc.latitude || 0}, {loc.longitude || 0}
                          </p>

                          {/* Services at this location */}
                          <div className="flex gap-4 text-sm">
                            {loc.activity_name && (
                              <div className="flex items-center text-orange-600">
                                <FaRunning className="mr-2" />
                                <div>
                                  <p className="font-semibold">{loc.activity_name}</p>
                                  <p className="text-xs">₹{loc.activity_price || 0}</p>
                                </div>
                              </div>
                            )}
                            {loc.hotel_name && (
                              <div className="flex items-center text-purple-600">
                                <FaHotel className="mr-2" />
                                <div>
                                  <p className="font-semibold">{loc.hotel_name}</p>
                                  <p className="text-xs">₹{loc.hotel_price || 0}/night</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Car Details */}
              {selectedTrip.car_name && (
                <div className="card p-4 bg-purple-50">
                  <h4 className="font-bold mb-2 flex items-center">
                    <FaCar className="mr-2" />
                    Selected Car
                  </h4>
                  <p className="font-semibold text-lg">{selectedTrip.car_name}</p>
                  {selectedTrip.car_description && (
                    <p className="text-sm text-gray-600">{selectedTrip.car_description}</p>
                  )}
                  <p className="text-lg font-bold text-green-600 mt-2">
                    ₹{selectedTrip.car_price || 0}
                  </p>
                </div>
              )}

              {/* Total Cost Breakdown */}
              <div className="card p-4 bg-green-50">
                <h4 className="font-bold mb-3">Cost Breakdown</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Mileage Charges</span>
                    <span className="font-semibold">₹{selectedTrip.mileage_charges || 0}</span>
                  </div>
                  {selectedTrip.car_price && (
                    <div className="flex justify-between">
                      <span>Car Rental</span>
                      <span className="font-semibold">₹{selectedTrip.car_price}</span>
                    </div>
                  )}
                  <div className="border-t-2 border-gray-300 pt-2 mt-2">
                    <div className="flex justify-between text-lg">
                      <span className="font-bold">Total Amount</span>
                      <span className="font-bold text-green-600">
                        ₹{selectedTrip.total_cost || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Admin Notes */}
              <div>
                <label className="label">Admin Notes</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="input-field"
                  rows="3"
                  placeholder="Add notes for this trip request..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                {selectedTrip.status === 'pending' && (
                  <>
                    <button
                      onClick={() => updateStatus('approved')}
                      className="btn-success flex-1 flex items-center justify-center"
                    >
                      <FaCheck className="mr-2" />
                      Approve Trip
                    </button>
                    <button
                      onClick={() => updateStatus('rejected')}
                      className="btn-danger flex-1 flex items-center justify-center"
                    >
                      <FaTimes className="mr-2" />
                      Reject Trip
                    </button>
                  </>
                )}
                {selectedTrip.status === 'approved' && (
                  <button
                    onClick={() => updateStatus('completed')}
                    className="btn-primary flex-1 flex items-center justify-center"
                  >
                    <FaCheck className="mr-2" />
                    Mark as Completed
                  </button>
                )}
                <button
                  onClick={() => deleteTrip(selectedTrip.id)}
                  className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center"
                >
                  <FaTrash className="mr-2" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomTripRequests
